/* eslint-disable import/namespace */
import React, { useEffect, useState } from "react";

import ManageStaffPage from "../../../../entities/DashboardPage/HRModule/ManageStaffPage";
import AuthService from "../../../../api/AuthService";
import LoaderHelper from "../../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../../context/AlertContext";

const ManageStaff = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(1);
  const [open, setOpen] = useState(false);
  const [departmentData, setDepartmentData] = useState([]);
  const [empData, setempData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState([]);
  const [permission, setPermission] = useState(null);
  const role = localStorage.getItem("role");
  const employeeId = localStorage.getItem("employeeId");
  const [depData, setDepData] = useState([]);
  const [getFilterVaue, setGetFilterValue] = useState("active");
  const [sort, setSort] = useState();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  // Department / role / joining-date filters; shared by the list request and the export
  const [listFilters, setListFilters] = useState({ department: "", role: "", startDate: "", endDate: "" });
  const [roleData, setRoleData] = useState([]);
  const { showAlert } = useAlert();
  const isAdmin = role === "Admin" || role === "admin" || role === "SuperAdmin" || role === "superadmin";

  // Fetch permissions from API only for Employee role
  const fetchPermissions = async () => {
    if ((role === "Employee" || role === "employee") && employeeId) {
      try {
        const permissionResult = await AuthService.getEmployeePermission(employeeId);
        if (permissionResult?.success && permissionResult?.data) {
          // API returns permissions directly in data object
          const permissions = permissionResult?.data;
          setPermission(permissions);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    } else {
      // For Admin/SuperAdmin, use localStorage
      const permissionObj = localStorage.getItem("permission");
      setPermission(permissionObj ? JSON.parse(permissionObj) : null);
    }
  };

  useEffect(() => {
    fetchPermissions();
    
    // Listen for permission updates (when admin changes permissions)
    const handlePermissionUpdate = () => {
      fetchPermissions();
    };
    
    window.addEventListener("permissionUpdated", handlePermissionUpdate);
    return () => {
      window.removeEventListener("permissionUpdated", handlePermissionUpdate);
    };
  }, []);

  useEffect(() => {
    if (permission !== null || role !== "Employee" && role !== "employee") {
      getEmployeeList(searchText, page, sort, getFilterVaue);
    }
  }, [page, sort, getFilterVaue, permission, listFilters])

  useEffect(() => {
    if (permission !== null || role !== "Employee" && role !== "employee") {
      getAllDepartmentList();
      getDepartment();
      getRoles();
    }
  }, [permission])
  
  const getEmployeeList = async (text, page, sort, getFilterVaue, type = "all") => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getEmployeeList(text, page, sort, getFilterVaue, type, listFilters);
      if (result?.success) {
        let employees = result?.data?.employees || [];
        
        // Filter data based on permissions for Employee role
        if ((role === "Employee" || role === "employee") && employeeId) {
          const hasViewPermission = permission?.employee?.isView === true;
          const hasEditPermission = permission?.employee?.isEdit === true;
          
          // If employee has edit permission, show all employees (like admin)
          if (hasEditPermission) {
            // Show all employees when edit permission is there
            setTotal(result?.data?.totalCount);
          } else if (hasViewPermission) {
            // If only view permission, show only own data
            employees = employees.filter((emp) => emp?._id === employeeId || emp?.userId === employeeId);
            setTotal(employees.length);
          } else {
            // No view permission, show nothing
            employees = [];
            setTotal(0);
          }
        } else {
          setTotal(result?.data?.totalCount);
        }
        
        setData(employees);
        setFilteredData(employees);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };
  const handleDeleteEmployee = async (id, value) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteEmployee(id, value);
      if (result?.success) {
        LoaderHelper.loaderStatus(false);
        setOpen(false);
        getEmployeeList(searchText, page, sort, getFilterVaue);
        showAlert(result?.message, "success")
      } else {
        LoaderHelper.loaderStatus(false);
        setOpen(false)
        showAlert(result?.message, "error")
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  }

  const handleSearch = (text) => {
    setSearchText(text);
    setPage(1);
    getEmployeeList(text, 1, sort, getFilterVaue);
  }
  const handleSort = (sort) => {
    sort && setSort(sort)
  }
  const handleStatusFilterChange = (value) => {
    setGetFilterValue(value || "active");
    setPage(1);
  };
  const handleListFiltersChange = (filters) => {
    setListFilters({ department: "", role: "", startDate: "", endDate: "", ...filters });
    setPage(1);
  };
  const handleEmployeePdfDownload = async () => {
    if (!isAdmin) return;

    const employeeStatus = getFilterVaue || "active";
    const reportStatus = employeeStatus === "inactive" ? "false" : "true";
    setPdfLoading(true);
    LoaderHelper.loaderStatus(true);
    try {
      // Export exactly what the list is currently showing
      const result = await AuthService.handleEmployeeReport(reportStatus, listFilters.department, "", "pdf", {
        ...listFilters,
        search: searchText,
      });

      let parsedResult = null;
      if (result instanceof Blob && result.type?.includes("application/json")) {
        try {
          parsedResult = JSON.parse(await result.text());
        } catch {
          parsedResult = null;
        }
      } else if (!(result instanceof Blob) && result?.success === false) {
        parsedResult = result;
      }

      if (parsedResult?.success === false) {
        showAlert(parsedResult?.message || "Unable to download employee list PDF.", "error");
        return;
      }

      const blob = result instanceof Blob ? result : new Blob([result], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `employee-list-${employeeStatus}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showAlert(error?.message || "Unable to download employee list PDF.", "error");
    } finally {
      setPdfLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };
  const getAllDepartmentList = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAllDepartmentList();
      if (result?.success) {
        setempData(result?.data);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };
  const getRoles = async () => {
    try {
      const result = await AuthService.getOperators(1, 100, true);
      if (result?.success) {
        setRoleData(result?.data?.operator || result?.data?.operators || []);
      }
    } catch (error) {
      setRoleData([]);
    }
  };
  const getDepartment = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getDepartment();
      if (result?.success) {
        setDepartmentData(result?.data?.position);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const SearchEmployee = async (text) => {
    setLoading(true);
    if (!text) {
      setLoading(false);
      return;
    }
    try {
      const result = await AuthService.getEmployeeList(text);
      if (result?.success) {
        setEmployeeData(result?.data?.employees);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };
  const DepartmentPayroll = async (department) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handlePayrollDepartmemt(department);
      if (result?.success) {
        setDepData(result?.data);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };
  const handleIncreament = async (employees, rate, date, fixed, increment) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleIncreament(employees, rate, date, fixed, increment);
      if (result?.success) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  }
  return (
    <ManageStaffPage SearchEmployee={SearchEmployee} data={filteredData.length > 0 ? filteredData : data} role={role} permission={permission} handleSort={handleSort} page={page} total={total} setPage={setPage} handleSearch={handleSearch} handleDeleteEmployee={handleDeleteEmployee}
      setData={setData} open={open} setOpen={setOpen} getEmployeeList={() => getEmployeeList(searchText, page, sort, getFilterVaue)}
      departmentData={departmentData} empData={empData} loading={loading} employeeData={employeeData} handleIncreament={handleIncreament}
      DepartmentPayroll={DepartmentPayroll} depData={depData} setGetFilterValue={handleStatusFilterChange} getFilterVaue={getFilterVaue} employeeId={employeeId}
      handleEmployeePdfDownload={handleEmployeePdfDownload} pdfLoading={pdfLoading}
      roleData={roleData} listFilters={listFilters} handleListFiltersChange={handleListFiltersChange} />
  )

}

export default ManageStaff;
